// Ordered list view of a route's stops. Each stop shows its sequence number,
// lead info, the leg distance/time (when a route has been generated), and the
// run-the-route actions: directions / call / sms / view / remove.
import React from 'react';
import styled from 'styled-components';
import {
  FaDirections, FaPhoneAlt, FaSms, FaUser, FaTrash, FaMapMarkerAlt, FaClock, FaExclamationTriangle,
} from 'react-icons/fa';
import formatPhone from '../../../components/shared/formatPhone';
import { formatStopAddress, stopQualifier, formatQty } from '../utils';
import { getUnitInfo } from '../../estimates/ServiceModal';
import { formatCurrency } from '../../../utils/utils';

const List = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface}; border-radius: 14px;
  border: 1px solid ${p => p.theme.colors.inputGray}22;
  padding: 14px; display: flex; gap: 12px;
`;

const Seq = styled.div`
  width: 30px; height: 30px; flex-shrink: 0; border-radius: 50%;
  background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 800; font-size: 0.9rem;
  display: flex; align-items: center; justify-content: center;
`;

const Body = styled.div`
  flex: 1; min-width: 0;
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 600; font-size: 0.98rem; }
  .row { display: flex; align-items: center; gap: 7px; color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem; margin-top: 4px; }
  .row svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; }
  .leg { display: inline-flex; align-items: center; gap: 5px; color: ${p => p.theme.colors.success}; font-size: 0.78rem; margin-top: 6px; font-weight: 600; }
  .nocoord { display: inline-flex; align-items: center; gap: 5px; color: ${p => p.theme.colors.error}; font-size: 0.76rem; margin-top: 6px; }
`;

const Actions = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 11px;
`;

const Act = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-height: 36px; padding: 0 12px; border-radius: 9px; cursor: pointer;
  border: 1px solid ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.inputGray) + '44'};
  background: transparent; font-size: 0.8rem; font-weight: 600;
  color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary};
  &:hover { border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary}; }
`;

// `stops` are route stops, not leads — the same lead can appear more than once
// with different addresses, so everything keys off stop.stopId.
const AddressTag = styled.span`
  display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 10px;
  font-size: 0.68rem; font-weight: 600; vertical-align: middle;
  background: ${p => p.theme.colors.primary}22; color: ${p => p.theme.colors.primary};
  border: 1px solid ${p => p.theme.colors.primary}44;
`;

const Items = styled.div`
  margin-top: 8px; border-top: 1px solid ${p => p.theme.colors.inputGray}22; padding-top: 4px;
  .head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: ${p => p.theme.colors.textSecondary}; margin-top: 6px; }
  .it { display: flex; justify-content: space-between; gap: 10px; padding: 4px 0; font-size: 0.84rem; color: ${p => p.theme.colors.textPrimary}; }
  .it .q { color: ${p => p.theme.colors.textSecondary}; font-size: 0.78rem; }
  .it .p { white-space: nowrap; }
  .none { font-size: 0.8rem; color: ${p => p.theme.colors.textSecondary}; margin-top: 6px; }
`;

const RouteStopList = ({ leads: stops, legs, onAction, onRemove, materials }) => {
  const quotesByStop = new Map((materials?.stopCards || []).map(c => [c.stop.stopId, c.quotes]));
  return (
    <List>
      {stops.map((stop, i) => {
        const address = formatStopAddress(stop);
        const leg = legs?.[i]; // travel from previous stop to this one
        const hasCoords = stop.latitude && stop.longitude;
        // Only worth showing when it disambiguates — a lead's primary address
        // needs no qualifier.
        const qualifier = stop.isPrimaryAddress ? null : stopQualifier(stop);
        const quotes = quotesByStop.get(stop.stopId); // undefined = not a converted stop
        return (
          <Card key={stop.stopId}>
            <Seq>{i + 1}</Seq>
            <Body>
              <div className="name">
                {stop.name || 'Lead'}
                {qualifier && <AddressTag>{qualifier}</AddressTag>}
              </div>
              {address && <div className="row"><FaMapMarkerAlt /> {address}</div>}
              {stop.phone && <div className="row"><FaPhoneAlt /> {formatPhone(stop.phone)}</div>}
              {leg && <span className="leg"><FaClock /> {leg.distance} · {leg.duration} from previous</span>}
              {!hasCoords && <span className="nocoord"><FaExclamationTriangle /> No map location — won't be optimized</span>}

              {quotes && (quotes.length === 0 ? (
                !materials.loading && <div className="none">No approved quote for this address</div>
              ) : (
                <Items>
                  <div className="head">Approved quote · {formatCurrency(quotes.reduce((t, q) => t + (Number(q.totalPrice) || 0), 0))}</div>
                  {quotes.flatMap(q => (q.items || []).map(it => (
                    <div className="it" key={`${q.id}-${it.id}`}>
                      <span>{it.Service?.service_name || it.observation || 'Item'} <span className="q">· {formatQty(it.totalArea)} {getUnitInfo(it.measurementUnit).short}</span></span>
                      <span className="p">{formatCurrency(it.totalPrice)}</span>
                    </div>
                  )))}
                </Items>
              ))}

              <Actions>
                <Act onClick={() => onAction('directions', stop.stopId)} disabled={!hasCoords && !address}><FaDirections /> Directions</Act>
                {stop.phone && <Act onClick={() => onAction('call', stop.stopId)}><FaPhoneAlt /> Call</Act>}
                {stop.phone && <Act onClick={() => onAction('sms', stop.stopId)}><FaSms /> SMS</Act>}
                <Act onClick={() => onAction('view', stop.stopId)}><FaUser /> Lead</Act>
                <Act $danger onClick={() => onRemove(stop.stopId)}><FaTrash /></Act>
              </Actions>
            </Body>
          </Card>
        );
      })}
    </List>
  );
};

export default RouteStopList;
